import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, MicOff, Send, Volume2, VolumeX, X, UserRound, Navigation, Subtitles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API, errMsg } from "@/lib/api";
import { AVATAR_SRC } from "@/components/clinic/SelvixAvatar";
import { playVoice, stopVoice } from "./voice";
import { JobCard } from "@/components/cafe/JobBoard";

const TAG = /\[\[REPARTO:([a-z0-9-]+)\]\]/i;
const sessionId = () => {
  let s = localStorage.getItem("selvix_chat_session");
  if (!s) {
    s = `s${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem("selvix_chat_session", s);
  }
  return s;
};

export function speak(text, onSub) {
  const clean = text.replace(TAG, "").replace(/\*\*|__|^#+\s/gm, "");
  onSub?.(clean);
  playVoice(clean, () => onSub?.(""));
}

function useSpeechInput(onText) {
  const [listening, setListening] = useState(false);
  const rec = useRef(null);
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  const toggle = () => {
    if (!SR) {
      toast.error("Riconoscimento vocale non supportato da questo browser");
      return;
    }
    if (listening) {
      rec.current?.stop();
      return;
    }
    const r = new SR();
    r.lang = "it-IT";
    r.interimResults = false;
    r.onresult = (e) => onText(e.results[0][0].transcript);
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    rec.current = r;
    r.start();
    setListening(true);
  };
  return { listening, toggle };
}

function Message({ m, departments, onGuide }) {
  const slug = m.text.match(TAG)?.[1];
  const dep = slug && departments.find((d) => d.slug === slug);
  const clean = m.text.replace(TAG, "").replace(/\*\*|__|^#+\s/gm, "").trim();
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${mine ? "bg-sky-600 text-white" : "border border-slate-200 bg-white text-slate-700"}`}>
        <p className="whitespace-pre-wrap">{clean || "…"}</p>
        {dep && (
          <button data-testid={`chat-guide-btn-${dep.slug}`} onClick={() => onGuide(dep)} className="mt-2 flex items-center gap-1.5 rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-800 transition-colors hover:bg-cyan-100">
            <Navigation className="h-3.5 w-3.5" /> Accompagnami: {dep.title}
          </button>
        )}
        {m.jobs?.length > 0 && <div className="mt-2 space-y-2" data-testid="chat-job-results">{m.jobs.map((j) => <JobCard key={j.id} j={j} compact />)}</div>}
      </div>
    </div>
  );
}

export function ChatPanel({ onClose, departments, onGuide, voiceDefault = true, onSubtitle, subs = false, setSubs = () => {} }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [voice, setVoice] = useState(voiceDefault);
  const endRef = useRef();
  const sid = useRef(sessionId());
  const seq = useRef(0);
  const mid = () => `m${(seq.current += 1)}`;
  const { listening, toggle } = useSpeechInput((t) => send(t));

  useEffect(() => {
    if (user) api.get(`/chat/history/${sid.current}`).then(({ data }) => setMsgs(data.map((m) => ({ ...m, id: mid() })))).catch(() => {});
  }, [user]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  async function send(raw) {
    const message = (raw ?? text).trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    const aid = mid();
    setMsgs((m) => [...m, { id: mid(), role: "user", text: message }, { id: aid, role: "assistant", text: "" }]);
    let full = "";
    let jobs = null;
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid.current, message }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Errore");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const d = p.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          const ev = JSON.parse(d);
          if (ev.jobs) jobs = ev.jobs;
          else full += ev.delta;
          setMsgs((m) => [...m.slice(0, -1), { id: aid, role: "assistant", text: full, jobs }]);
        }
      }
      if (voice) speak(full, subs ? onSubtitle : undefined);
    } catch (e) {
      setMsgs((m) => [...m.slice(0, -1), { id: aid, role: "assistant", text: `⚠ ${e.message}` }]);
    } finally {
      setBusy(false);
    }
  }

  const handoff = async () => {
    try {
      await api.post("/chat/handoff", { session_id: sid.current, message: text || "Il paziente chiede di parlare con un operatore." });
      toast.success("Richiesta inoltrata alla segreteria. Verrai ricontattato.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="selvix-chat-panel" className="pointer-events-auto glass absolute inset-x-2 bottom-2 top-20 z-40 flex flex-col overflow-hidden rounded-3xl sm:inset-x-auto sm:bottom-6 sm:left-6 sm:top-auto sm:h-[min(620px,calc(100vh-8rem))] sm:w-[400px] rise">
      <div className="flex items-center gap-3 border-b border-slate-200/70 px-4 py-3">
        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full border border-cyan-200 bg-cyan-50">
          <img src={AVATAR_SRC} alt="Doctor Selvix" className="h-[180%] w-full object-contain object-top" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold text-slate-900">Doctor Selvix</p>
          <p className="text-[11px] text-cyan-700">Assistente AI · non è un medico</p>
        </div>
        <button data-testid="chat-subtitles-toggle" onClick={() => setSubs(!subs)} className={`grid h-8 w-8 place-items-center rounded-full transition-colors ${subs ? "text-sky-700 bg-sky-50" : "text-slate-400"}`} aria-label="Sottotitoli"><Subtitles className="h-4 w-4" /></button>
        <button data-testid="chat-voice-toggle" onClick={() => { setVoice(!voice); stopVoice(); }} className={`grid h-8 w-8 place-items-center rounded-full transition-colors ${voice ? "text-sky-700 bg-sky-50" : "text-slate-400"}`} aria-label="Voce">{voice ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}</button>
        <button data-testid="chat-close-btn" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full text-slate-500 transition-colors hover:bg-slate-100" aria-label="Chiudi"><X className="h-4 w-4" /></button>
      </div>
      <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-[11px] leading-snug text-red-700" data-testid="chat-disclaimer">
        Doctor Selvix AI non è un medico, non effettua diagnosi e non prescrive farmaci. Non inserire dati sanitari personali. In caso di emergenza chiama subito il <b>112</b>.
      </div>
      {!user ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm text-slate-600">Per conversare con Doctor Selvix accedi o crea un account gratuito.</p>
          <Link data-testid="chat-login-link" to="/accedi?next=/" className="rounded-full bg-sky-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700">Accedi</Link>
        </div>
      ) : (
        <>
          <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50/60 p-4" data-testid="chat-messages">
            {msgs.length === 0 && (
              <div className="space-y-2">
                {["Quali servizi offre la clinica?", "Come prenoto un video consulto?", "Come richiedo una ricetta?"].map((q) => (
                  <button key={q} data-testid="chat-suggestion" onClick={() => send(q)} className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-medium text-slate-600 transition-colors hover:border-cyan-400">{q}</button>
                ))}
              </div>
            )}
            {msgs.map((m) => <Message key={m.id} m={m} departments={departments} onGuide={onGuide} />)}
            <div ref={endRef} />
          </div>
          <div className="border-t border-slate-200/70 p-3">
            <div className="flex items-center gap-2">
              <button data-testid="chat-mic-btn" onClick={toggle} className={`grid h-10 w-10 shrink-0 place-items-center rounded-full border transition-colors ${listening ? "border-red-300 bg-red-50 text-red-600" : "border-slate-200 bg-white text-slate-500 hover:text-sky-700"}`} aria-label="Parla">
                {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
              <input data-testid="selvix-ai-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi a Doctor Selvix…" className="h-10 min-w-0 flex-1 rounded-full border border-slate-200 bg-white px-4 text-sm outline-none focus:border-cyan-400" />
              <button data-testid="chat-send-btn" disabled={busy} onClick={() => send()} className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-sky-600 text-white transition-colors hover:bg-sky-700 disabled:opacity-50" aria-label="Invia"><Send className="h-4 w-4" /></button>
            </div>
            <button data-testid="chat-handoff-btn" onClick={handoff} className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition-colors hover:text-sky-700">
              <UserRound className="h-3.5 w-3.5" /> Parla con un operatore
            </button>
          </div>
        </>
      )}
    </div>
  );
}
